import { client as clientV1 } from "@sgdf/client";
import { client as clientV2 } from "@sgdf/client-v2";
import { apiUrl } from "~~/plugins/api.service";
import {
  authenticationService,
  getSessionVersion,
  tokenManager,
} from "./services/authentication.service";
import { ApiError, INLINE_ERROR_HEADER } from "./utils/api";

/** Auth endpoints (login, refresh). On 401 we don't want to do the
 * "session expired" routine (logout + redirect) — a 401 here just means
 * the user typed a wrong password. We still want to surface the error
 * as a toast though. */
const isAuthEndpoint = (url: string): boolean =>
  url.includes("/api/auth/jwt/");

/** Translate i18n keys returned by the backend or rejected by the auth
 * service. Supports two shapes:
 *   - clés dottées DRF v1 (ex. ``message.user_not_authorized_backoffice``)
 *   - codes kebab-case ninja v2 ``BusinessRuleError`` (ex. ``wrong-meeting``)
 *     qui sont mappés automatiquement vers ``error.<code>`` dans le locale.
 * Retourne la valeur brute si la clé n'est pas définie, ou si on est hors
 * contexte Nuxt (SSR / tests). */
const tryTranslate = (key: string): string => {
  if (typeof key !== "string") return key;
  // Codes kebab-case (sans point) → cherchent sous ``error.<code>``.
  // Phrases libres (avec espaces) → retournées telles quelles, pas la
  // peine de payer une lookup i18n.
  const isDotted = key.includes(".");
  const isKebabCode = !isDotted && /^[a-z][a-z0-9-]*$/.test(key);
  if (!isDotted && !isKebabCode) return key;
  const lookupKey = isDotted ? key : `error.${key}`;
  try {
    const i18n = useNuxtApp().$i18n as
      | { global?: { t: (k: string) => string } }
      | undefined;
    const translated = i18n?.global?.t(lookupKey);
    return translated && translated !== lookupKey ? translated : key;
  } catch {
    return key;
  }
};

/** Extract a human-readable ``{title, detail}`` pair from the parsed
 * error body returned by the API. Translates i18n keys when found.
 *
 * Supported shapes:
 * - django-ninja v2 :   ``{ detail, code }``  (BusinessRuleError)
 * - DRF v1 errors :     ``{ detail }`` or ``{ non_field_errors: [...] }`` or
 *                       ``{ field: [errors] }``
 * - SimpleJWT 401 :     ``{ detail, code }``
 * - opaque string body
 */
/** Default translated "Erreur" title for toasts. */
const errorTitle = () => tryTranslate("notification.title.error");

/** Message générique d'un statut HTTP, quand la réponse n'en donne pas de
 * lisible (page HTML d'un proxy, phrase anglaise par défaut du framework). */
const statusDetail = (status: number): string => {
  if (status >= 500) return tryTranslate("message.internal_server_error");
  if (status === 403) return tryTranslate("message.forbidden");
  if (status === 404) return tryTranslate("message.not_found");
  if (status === 413) return tryTranslate("message.payload_too_large");
  if (status === 429) return tryTranslate("message.too_many_requests");
  return tryTranslate("message.unknown_error");
};

/** Phrases par défaut de django-ninja, DRF et Django (`get_object_or_404`) :
 * en anglais, sans information pour la personne. */
const FRAMEWORK_DEFAULT_DETAILS = [
  /^Forbidden$/,
  /^Not Found$/i,
  /^Not found\.$/,
  /^Method not allowed$/i,
  /^You do not have permission to perform this action\.$/,
  /^No .+ matches the given query\.$/,
];

const isFrameworkDefault = (detail: string) =>
  FRAMEWORK_DEFAULT_DETAILS.some((pattern) => pattern.test(detail));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractError = (resp: any, status: number) => {
  // Tout 5xx : 500 de Django, mais aussi 502/503/504 du proxy (corps HTML).
  if (status >= 500) {
    return { title: errorTitle(), detail: statusDetail(status) };
  }
  if (!resp || typeof resp !== "object") {
    // Corps texte : page HTML (413 de nginx…) ou vide → message du statut.
    const text = String(resp ?? "").trim();
    const readable = text && !text.startsWith("<") && text.length <= 300;
    return {
      title: errorTitle(),
      detail: readable ? tryTranslate(text) : statusDetail(status),
    };
  }
  // Root-level array of messages (DRF list serializer / non_field_errors
  // returned at root). Without this branch ``Object.entries`` would yield
  // ``[["0", "<msg>"]]`` and the toast would surface a useless ``"0 : <msg>"``.
  if (Array.isArray(resp)) {
    return {
      title: errorTitle(),
      detail: resp.map((m) => tryTranslate(String(m))).join(" "),
    };
  }
  // django-ninja v2 / SimpleJWT — ``{ detail, code }``
  if (typeof resp.detail === "string" && isFrameworkDefault(resp.detail)) {
    return { title: errorTitle(), detail: statusDetail(status) };
  }
  if (typeof resp.detail === "string") {
    return {
      title: resp.code ? tryTranslate(String(resp.code)) : errorTitle(),
      detail: tryTranslate(resp.detail),
    };
  }
  if (Array.isArray(resp.detail)) {
    // django-ninja 422 brut : liste d'objets pydantic ({ loc, msg }). Sans
    // extraire ``.msg`` chaque item donnerait « [object Object] ».
    return {
      title: errorTitle(),
      detail: resp.detail
        .map((item: unknown) =>
          item && typeof item === "object" && "msg" in item
            ? tryTranslate(String((item as { msg?: unknown }).msg))
            : tryTranslate(String(item)),
        )
        .join(" "),
    };
  }
  // DRF: ``non_field_errors`` (often i18n keys, cf. auth service rejects)
  if (Array.isArray(resp.non_field_errors) && resp.non_field_errors.length) {
    return {
      title: errorTitle(),
      detail: resp.non_field_errors
        .map((m: unknown) => tryTranslate(String(m)))
        .join(" "),
    };
  }
  // DRF parfois sérialise une liste d'erreurs comme un objet à clés
  // numériques (``{ "0": "msg" }``) — on le rétablit comme un array
  // pour ne pas surfacer un préfixe ``"0 : "`` inutile dans le toast.
  const entries = Object.entries(resp);
  if (entries.length > 0 && entries.every(([k]) => /^\d+$/.test(k))) {
    return {
      title: errorTitle(),
      detail: entries
        .map(([, v]) =>
          Array.isArray(v)
            ? v.map((m) => tryTranslate(String(m))).join(" ")
            : tryTranslate(String(v)),
        )
        .join(" "),
    };
  }
  // DRF: field errors (``{ password: ["too short"] }``). Single-field
  // → on n'affiche pas le nom du champ (l'utilisateur sait quel champ
  // il vient de modifier) ; multi-champs → on garde le préfixe pour
  // distinguer.
  if (entries.length === 1) {
    const [, v] = entries[0]!;
    const msgs = Array.isArray(v)
      ? v.map((m) => tryTranslate(String(m))).join(" ")
      : tryTranslate(String(v));
    return { title: errorTitle(), detail: msgs };
  }
  const fieldMsgs = entries
    .map(([k, v]) => {
      const msgs = Array.isArray(v)
        ? v.map((m) => tryTranslate(String(m))).join(", ")
        : tryTranslate(String(v));
      return `${k} : ${msgs}`;
    })
    .join(" | ");
  return {
    title: errorTitle(),
    detail: fieldMsgs || tryTranslate("message.unknown_error"),
  };
};

/**
 * Generic request interceptor shared by the v1 (DRF) and v2 (django-ninja)
 * clients. Attaches a JWT or QRCode header, refreshing the JWT on expiry.
 * Auth requests skip session cleanup; other requests remember their session
 * so an old 401 cannot disconnect the next user.
 */
const requestSessions = new WeakMap<Request, number>();

/** Message d'une erreur affichée sur place (appel avec `INLINE_ERROR_HEADER`) :
 * le même texte que le toast qu'elle remplace. `null` pour un 401 : la
 * session expirée est déjà gérée ici (retour à la connexion). */
export const inlineErrorMessage = (error: unknown): string | null => {
  if (!(error instanceof ApiError) || error.status === undefined) {
    // `fetch` rejeté (réseau coupé, CORS) : aucune réponse à interpréter.
    return tryTranslate("message.network_error");
  }
  if (error.status === 401) return null;
  return extractError(error.apiErrors, error.status).detail;
};

// Requêtes dont l'appelant affiche lui-même l'erreur (`INLINE_ERROR_HEADER`).
const inlineErrorRequests = new WeakSet<Request>();
const recordRequestSession = (request: Request, version = getSessionVersion()) => {
  requestSessions.set(request, version);
  return request;
};

const buildRequestInterceptor =
  () =>
  async (request: Request): Promise<Request> => {
    if (request.headers.has(INLINE_ERROR_HEADER)) {
      request.headers.delete(INLINE_ERROR_HEADER);
      inlineErrorRequests.add(request);
    }
    if (isAuthEndpoint(request.url)) return request;
    const version = getSessionVersion();

    const access_token = localStorage.getItem("access_token");
    const refresh_token = localStorage.getItem("refresh_token");
    const delay_seconds = 10;

    if (
      access_token &&
      !tokenManager.isTokenExpired(access_token, delay_seconds)
    ) {
      request.headers.set("Authorization", `JWT ${access_token}`);
      return recordRequestSession(request);
    }

    if (
      refresh_token &&
      !tokenManager.isTokenExpired(refresh_token, delay_seconds)
    ) {
      const resp = await authenticationService.refreshToken();
      if (version !== getSessionVersion()) return recordRequestSession(request, version);
      if (resp) {
        request.headers.set("Authorization", `JWT ${resp.access}`);
        return recordRequestSession(request);
      }
    }

    const currentMeetingId = localStorage.getItem("current_meeting");
    if (currentMeetingId) {
      const qrCode = localStorage.getItem(`qrcode_token:${currentMeetingId}`);
      if (qrCode) {
        request.headers.set("Authorization", `QRCode ${qrCode}`);
        return recordRequestSession(request);
      }
    }

    if (access_token || refresh_token) authenticationService.logout();
    return recordRequestSession(request);
  };

// Partagé par les clients v1 et v2 : une rafale de 401 ne doit lancer
// qu'une redirection, en conservant la destination du premier appel.
let sessionRedirect: Promise<void> | undefined;

/** Shared response interceptor: surfaces server errors as toast notifications
 * and triggers a logout/redirect on 401 (only for non-auth endpoints —
 * a 401 from the login route is just "wrong credentials").
 *
 * Forms / pages MUST NOT add their own toast in their ``catch`` blocks —
 * the interceptor is the single source of error toasts. A call that shows
 * its error inline instead sends ``INLINE_ERROR_HEADER``. They only handle
 * UI-state side effects (loading flags, success navigation, …). */
/** Rendu commun d'une réponse en erreur, pour les clients API comme pour
 * les envois de fichiers (`$fetch`, cf. `composables/file.ts`) : un 401 hors
 * connexion renvoie vers la connexion, le reste devient un toast — sauf pour
 * un appel qui affiche lui-même son erreur (`inline`). */
export const reportErrorResponse = async ({
  status,
  url,
  body,
  request,
  inline = false,
}: {
  status: number;
  url: string;
  body: unknown;
  request?: Request;
  inline?: boolean;
}): Promise<void> => {
  const notifyStore = useNotifyStore();
  if (status === 401 && !isAuthEndpoint(url)) {
    // Une ancienne requête ne doit pas déconnecter la session suivante.
    if (request && requestSessions.get(request) !== getSessionVersion()) return;
    authenticationService.logout();
    if (new URL(window.location.href).pathname !== RouteUtils.login) {
      sessionRedirect ??= (async () => {
        notifyStore.notifyDetailed(
          "Session expirée",
          "Votre session a expiré. Veuillez vous reconnecter.",
          NotificationType.Warning,
        );
        try {
          await navigateTo({
            path: RouteUtils.login,
            query: { next: extractPathname(window.location.href) },
          });
        } finally {
          sessionRedirect = undefined;
        }
      })();
      await sessionRedirect;
    }
    return;
  }

  if (inline) return;

  let { title, detail } = extractError(body, status);
  if (status === 401 && isAuthEndpoint(url)) {
    // Don't leak SimpleJWT's English detail ("No active account…"); show a
    // single, localized message for failed credentials.
    title = errorTitle();
    detail = tryTranslate("message.user_not_authorized_backoffice");
  }
  notifyStore.notifyDetailed(title, detail, NotificationType.Danger);
};

const buildResponseInterceptor =
  () =>
  async (response: Response, request: Request): Promise<Response> => {
    if (response.ok) return response;
    const contentType = response.headers.get("content-type");
    const body = await (contentType?.includes("application/json")
      ? response.clone().json()
      : response.clone().text()
    ).catch(() => null);
    await reportErrorResponse({
      status: response.status,
      url: response.url,
      body,
      request,
      inline: inlineErrorRequests.has(request),
    });
    return response;
  };

// Réseau coupé : toutes les requêtes en cours échouent ensemble, un seul toast.
const NETWORK_TOAST_INTERVAL_MS = 5000;
let lastNetworkToastAt = 0;

/** Toast d'une requête restée sans réponse (réseau coupé, CORS). */
export const reportNetworkError = (): void => {
  const now = Date.now();
  if (now - lastNetworkToastAt <= NETWORK_TOAST_INTERVAL_MS) return;
  lastNetworkToastAt = now;
  useNotifyStore().notifyDetailed(
    errorTitle(),
    tryTranslate("message.network_error"),
    NotificationType.Danger,
  );
};

/** Shared error interceptor: hey-api calls it for every failed request,
 * including those that got **no response** (network down, CORS) — the
 * response interceptor never sees them, so without this they fail silently. */
const buildErrorInterceptor =
  () =>
  (
    error: unknown,
    response: Response | undefined,
    request: Request | undefined,
  ) => {
    const aborted = error instanceof DOMException && error.name === "AbortError";
    const inline = request !== undefined && inlineErrorRequests.has(request);
    if (response === undefined && !aborted && !inline) reportNetworkError();
    return error;
  };

let v1InterceptorsRegistered = false;
let v2InterceptorsRegistered = false;

export const configureApiClient = () => {
  clientV1.setConfig({ baseUrl: apiUrl ?? "" });
  if (v1InterceptorsRegistered) return;
  v1InterceptorsRegistered = true;
  clientV1.interceptors.request.use(buildRequestInterceptor());
  clientV1.interceptors.response.use(buildResponseInterceptor());
  clientV1.interceptors.error.use(buildErrorInterceptor());
};

/** Configure the django-ninja /api/v2 client. Same base URL and same
 * auth/error interceptors as the v1 DRF client — the two cohabit during
 * the migration. */
export const configureApiClientV2 = () => {
  clientV2.setConfig({ baseUrl: apiUrl ?? "" });
  if (v2InterceptorsRegistered) return;
  v2InterceptorsRegistered = true;
  clientV2.interceptors.request.use(buildRequestInterceptor());
  clientV2.interceptors.response.use(buildResponseInterceptor());
  clientV2.interceptors.error.use(buildErrorInterceptor());
};

export const extractPathname = (
  urlString: string,
  fallback: string = RouteUtils.home,
): string => {
  try {
    const url = new URL(urlString);
    return url.pathname;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return urlString || fallback;
  }
};
